import type { ExternalValueProperty, PrimitiveValueName } from "@skilitics/nautilus-core";

// Specific import: the subtype instance only, never its definition — the
// definition's Comparison View would pull the whole values UI in behind a const.
import { localeSubtype } from "./subtype-definitions/locale/localeSubtype.ts";
import type { ValueSubtype } from "./valueSubtypes.ts";
import type { ValueSubtypeSpec } from "./valueSubtypeSpecs.ts";

/**
 * An external (environment) value contributed to value pickers by the
 * framework alongside node values. Read-only: a value source, never an effect
 * target. Resolved to an `envProperty` resolvable (conditions) or an `ext`
 * `__dv` reference (runtime values).
 */
export interface ExternalValueDescriptor {
  property: ExternalValueProperty;
  type: PrimitiveValueName;
  /**
   * Narrows the value as a node value's does, and is worth declaring only where
   * the designer holds the authoritative list — a narrowing drawn from anything
   * less would silently reject valid bindings. `locale` qualifies: it is the
   * module's content languages, which are state.
   */
  subtype?: ValueSubtype;
  /** Constrains a literal compared with this value, as a node value's does. */
  subtypeSpec?: ValueSubtypeSpec;
  title: string;
  /** used until runtime provides the real value */
  fallback: string;
  /**
   * Whether the value picker offers it. `false` withdraws it from the picker
   * while leaving it resolvable, so configs that already reference it keep
   * their type, subtype, title and runtime binding.
   */
  offered?: boolean;
}

// hard-coded for now, mirroring the environment binding options
export const externalValueDescriptors: readonly ExternalValueDescriptor[] = [
  { property: "learner.givenName", type: "string", title: "Given Name", fallback: "Jane" },
  { property: "learner.familyName", type: "string", title: "Family Name", fallback: "Doe" },
  { property: "locale", type: "string", subtype: localeSubtype(), title: "Active Locale", fallback: "en" },
];

export const getExternalValueDescriptor = (property: string): ExternalValueDescriptor | undefined =>
  externalValueDescriptors.find((descriptor) => descriptor.property === property);
